export type TranscriptUtterance = {
  speaker: number;
  text: string;
  translation?: string;
  timestamp?: string;
};

type IsoLike = string | number | Date | undefined;

const normalizeTokens = (text: string): Set<string> =>
  new Set(
    String(text || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((w) => w.length >= 3),
  );

const toIso = (v: IsoLike): string | undefined => {
  if (!v) return undefined;
  const t = new Date(v as any).getTime();
  if (!Number.isFinite(t)) return undefined;
  return new Date(t).toISOString();
};

/**
 * Canonical English transcript for analysis (insights/reports/suggestions).
 * - Interpreter ON: S1 uses text, S2 uses translation->English fallback to text.
 * - Interpreter OFF: use originals (source language) only.
 */
export function buildAnalysisTranscript(
  utterances: readonly TranscriptUtterance[],
  interpreterEnabled: boolean,
): string {
  const uts = utterances || [];

  if (interpreterEnabled) {
    var  output: string = uts.map((u) => (u.speaker === 1 ? u.text : u.translation || u.text)).join(" ");
    console.log("output", output);
    return output;
  }
  return uts.map((u) => u.text).join(" ");
}

export function buildTranslatedTranscript(
  utterances: readonly TranscriptUtterance[],
  interpreterEnabled: boolean,
): string {
  const uts = utterances || [];

  if (interpreterEnabled) {
    var  output: string = uts.map((u) => (u.speaker === 1 ?  u.translation  : u.text)).join(" ");
    console.log("output", output);
    return output;
  }
  return uts.map((u) => u.text).join(" ");
}

/**
 * Canonical English transcript with speaker tags for grounding (extractAnswer).
 */
export function buildAnalysisTranscriptTagged(
  utterances: readonly TranscriptUtterance[],
  interpreterEnabled: boolean,
): string {
  const uts = utterances || [];
  const toEnglish = (u: TranscriptUtterance) =>
    interpreterEnabled ? (u.speaker === 1 ? u.text : u.translation || u.text) : u.text;
  return uts.map((u) => `[Party ${u.speaker}]: ${toEnglish(u)}`).join("\n");
}

/**
 * Build a bounded answer window starting at askedAt until the next interviewer turn.
 */
export function buildAnswerWindowFromAskedAt(
  utterances: readonly TranscriptUtterance[],
  askedAtIso: IsoLike,
  interpreterEnabled: boolean,
  hardCapMs: number = 45_000,
): string {
  const startMs = new Date(askedAtIso as any).getTime();
  if (!Number.isFinite(startMs)) return "";
  const capMs = startMs + hardCapMs;

  const toEnglish = (u: TranscriptUtterance) =>
    interpreterEnabled ? (u.speaker === 1 ? u.text : u.translation || u.text) : u.text;

  const lines: string[] = [];
  for (const u of utterances || []) {
    if (!u.timestamp) continue;
    const t = new Date(u.timestamp).getTime();
    if (t < startMs) continue;
    if (t > capMs) break;
    if (u.speaker === 1 && t > startMs) break;
    lines.push(`[Party ${u.speaker}]: ${toEnglish(u)}`);
  }

  return lines.join("\n");
}

/**
 * Best-effort refinement: if askedAt came from AI, scan nearby for a likely interviewer question.
 * Returns an ISO string when possible; otherwise returns the original value.
 */
export function refineAiAskedAt(
  utterances: readonly TranscriptUtterance[],
  questionText: string,
  approxAskedAt: IsoLike,
): string | undefined {
  const approxMs = new Date(approxAskedAt as any).getTime();
  if (!Number.isFinite(approxMs)) return toIso(approxAskedAt);

  const windowBeforeMs = 60_000;
  const windowAfterMs = 10_000;
  const start = approxMs - windowBeforeMs;
  const end = approxMs + windowAfterMs;

  const qTokens = normalizeTokens(questionText);
  let bestMs = approxMs;
  let bestScore = 0;

  for (const u of utterances || []) {
    if (!u.timestamp || u.speaker !== 1) continue;
    const t = new Date(u.timestamp).getTime();
    if (t < start || t > end) continue;

    const text = String(u.text || "").toLowerCase();
    const hasQ =
      /\?|\b(what|when|where|how|has|did|do you|can you|is there|tell me)\b/.test(text);

    const uTokens = normalizeTokens(text);
    let overlap = 0;
    qTokens.forEach((w) => {
      if (uTokens.has(w)) overlap++;
    });
    const score = overlap / Math.max(1, qTokens.size) + (hasQ ? 0.2 : 0);
    if (score > bestScore) {
      bestScore = score;
      bestMs = t;
    }
  }

  // If we found a decent match, anchor to it; else stick with approx.
  const anchored = bestScore >= 0.3 ? bestMs : approxMs;
  return new Date(anchored).toISOString();
}

